/**
 * Runtime configuration, read from the `nlocal-config` meta tag the Blade shell renders.
 *
 * Deliberately not `import.meta.env`: Vite inlines those at build time, so a single built
 * bundle could not be promoted from staging to production without rebuilding. Reading from
 * the server-rendered document means the same `public/build` artifact runs anywhere.
 */
export interface ReverbConfig {
  key: string
  host: string
  port: number
  scheme: 'http' | 'https'
}

export interface SentryConfig {
  /** Empty disables browser reporting entirely — the default outside staging/production. */
  dsn: string
  environment: string
  traces_sample_rate: number
}

export interface AppConfig {
  appName: string
  /** Origin the API is served from. Same origin as the SPA — see DECISIONS.md D-009. */
  apiUrl: string
  /**
   * The absolute docs root the *server* uses, not the in-app route.
   *
   * Every §7.5 error code resolves to `{docsUrl}/errors#CODE`, and that link is printed by the
   * CLI in a terminal as well as rendered here — so it has to stay absolute even though the
   * dashboard's own Docs links now go to `/docs` in this SPA (D-024 clause 10). The SPA renders
   * whatever `error.docs` the API sends rather than building the URL itself, which is why
   * nothing in `resources/js` reads this today.
   */
  docsUrl: string
  /** The domain tunnels hang off: `checkout.{rootDomain}` (§8.5, D-016). */
  rootDomain: string
  reverb: ReverbConfig
  sentry: SentryConfig
}

const FALLBACK: AppConfig = {
  appName: 'nlocal',
  apiUrl: window.location.origin,
  docsUrl: 'https://nlocal.dev/docs',
  rootDomain: 'nlocal.dev',
  reverb: { key: '', host: window.location.hostname, port: 8080, scheme: 'http' },
  sentry: { dsn: '', environment: 'local', traces_sample_rate: 0 },
}

function read(): AppConfig {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="nlocal-config"]')

  if (meta === null) return FALLBACK

  try {
    return { ...FALLBACK, ...(JSON.parse(meta.content) as Partial<AppConfig>) }
  } catch {
    // A malformed tag must not take the whole dashboard down; same-origin defaults work.
    return FALLBACK
  }
}

export const config: AppConfig = read()
